import type { DockPanel } from "../../../../stores/dock";

/**
 * The files kind's payload (bots B1, 2026-09-03): a project WORKSPACE
 * browser — `workspace/` (a bot's durable working directory), `downloads/`
 * and `output/` — living in the dock beside terminal and artifacts, per the
 * owner rule that results and inspectors are dock panels, never page-local.
 *
 * `project` may be empty: the panel then offers the project picker first.
 */
export type WorkspaceArea = "workspace" | "downloads" | "output";
export const WORKSPACE_AREAS: readonly WorkspaceArea[] = ["workspace", "downloads", "output"] as const;

export interface FilesData {
	project: string;
	area: WorkspaceArea;
	path: string; // sub-path inside the area ("" = area root)
}

/** Read a files panel's payload. Total by construction — `data` is `unknown`
 *  on DockPanel, so exactly one place narrows it. */
export function filesData(panel: DockPanel): FilesData {
	const d = panel.data as Partial<FilesData> | undefined;
	const area = d?.area && (WORKSPACE_AREAS as readonly string[]).includes(d.area) ? d.area : "workspace";
	return { project: d?.project ?? "", area, path: d?.path ?? "" };
}

/** The physical path the /api/vault/browse endpoints understand. */
export function browsePath(project: string, area: WorkspaceArea, sub = ""): string {
	const base = `agntspace-projects/${project}/${area}`;
	const rel = sub.replace(/\\/g, "/").replace(/^\/+|\/+$/g, "");
	return rel ? `${base}/${rel}` : base;
}

/** ONE files panel per project (stable id → re-opening focuses the tab). */
export function filesPanel(project = ""): DockPanel {
	return {
		id: project ? `files:${project}` : "files:picker",
		kind: "files",
		title: project ? `Files · ${project}` : "Files",
		data: { project, area: "workspace", path: "" } satisfies FilesData,
		// Global: a workspace is reachable from any module, like the terminal.
	};
}

/**
 * Split a typed command line into argv the way a POSIX shell would tokenise
 * it — quotes group, backslash escapes — WITHOUT interpreting anything else.
 * There is no shell behind /api/run-command, so `|`, `>` and `&&` are just
 * characters in an argument; the server shows the user exactly what it got.
 */
export function splitArgv(line: string): string[] {
	const out: string[] = [];
	let cur = "";
	let quote: '"' | "'" | null = null;
	let had = false;
	for (let i = 0; i < line.length; i += 1) {
		const ch = line.charAt(i);
		if (quote) {
			if (ch === quote) {
				quote = null;
			} else if (ch === "\\" && quote === '"' && i + 1 < line.length) {
				i += 1;
				cur += line.charAt(i);
			} else {
				cur += ch;
			}
			continue;
		}
		if (ch === '"' || ch === "'") {
			quote = ch;
			had = true;
		} else if (ch === "\\" && i + 1 < line.length) {
			i += 1;
			cur += line.charAt(i);
			had = true;
		} else if (/\s/.test(ch)) {
			if (cur || had) out.push(cur);
			cur = "";
			had = false;
		} else {
			cur += ch;
			had = true;
		}
	}
	if (cur || had) out.push(cur);
	return out;
}

/** Markdown for a command's result — the dock artifact the chat and the
 *  Files panel both push, so the two surfaces cannot render one run
 *  differently. */
export function commandResultMarkdown(r: {
	command: string;
	cwd: string;
	exit_code: number | null;
	stdout: string;
	stderr: string;
	truncated?: boolean;
	timed_out?: boolean;
	duration_ms?: number;
}): string {
	const status = r.timed_out ? "timed out" : `exit ${r.exit_code ?? "?"}`;
	const parts = [
		`\`\`\`\n$ ${r.command}\n\`\`\``,
		`_in \`${r.cwd}\` · ${status}${typeof r.duration_ms === "number" ? ` · ${r.duration_ms} ms` : ""}${r.truncated ? " · output truncated" : ""}_`,
	];
	if (r.stdout) parts.push(`**stdout**\n\n\`\`\`\n${r.stdout}\n\`\`\``);
	if (r.stderr) parts.push(`**stderr**\n\n\`\`\`\n${r.stderr}\n\`\`\``);
	if (!r.stdout && !r.stderr) parts.push("_(no output)_");
	return parts.join("\n\n");
}
